import { useEffect, useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RainbowButton } from "@/components/ui/rainbow-button";
import BrandAnalysisSlideshow from "@/components/custom-ui/brand-analysis-slideshow";
import { supabase } from '@/lib/supabase';
import { 
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Search,
  Star,
  MessageSquare,
  Users,
  Zap,
  Package
} from "lucide-react";

export interface BrandData {
  id: string;
  url?: string;
  scraping_status?: string;
  brand_analysis_status?: string;
  brand_name?: string;
  brand_data: {
    brand_name?: string;
    elevator_pitch?: string;
    target_market?: string;
    value_props?: string;
    differentiators?: string;
    brand_voice?: string;
    call_to_action?: string;
    niche?: string;
    target_geography?: string;
    products_overview?: string;
    b2b_b2c?: string;
  };
  metadata?: {
    images?: Array<{
      url: string;
      description?: string;
      dimensions?: {
        width: number;
        height: number;
      };
    }>;
    css?: any;
    analysis_generated_at?: string;
    analysis_model?: string;
  };
  scraping_results?: {
    timestamp: string;
    raw_content: string;
    extracted_info: any;
  };
  brand_analysis_results?: AIAnalysisResponse;
}

interface DetailedInsight {
  id: string;
  title: string;
  summary: string;
  strengths: {
    quote: string;
    items: string[];
  };
  opportunities: {
    quote: string;
    items: string[];
  };
  weaknesses?: {
    quote: string;
    items: string[];
  };
  elaboration: string;
}

interface AIAnalysisResponse {
  summaryInsight: string;
  detailedInsights: DetailedInsight[];
  integratedRecommendations: string[];
}

interface BrandAnalysisComponentProps {
  brandData: BrandData;
  isAnonymous?: boolean;
  sessionToken?: string;
  onClaimBrand?: () => void;
  onComplete?: () => void;
  showClaimButton?: boolean;
}

export default function BrandAnalysisComponent({
  brandData,
  isAnonymous = false,
  onClaimBrand,
  onComplete,
  showClaimButton = true
}: BrandAnalysisComponentProps) {
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResponse | null>(
    brandData.brand_analysis_results || null
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSlideAutoPlaying, setIsSlideAutoPlaying] = useState(true);
  const [showDeeperInsights, setShowDeeperInsights] = useState(false);
  const slideIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Define slideshow scenes based on brand data
  const getSlideScenes = () => {
    if (!brandData?.brand_data) return [];
    
    const scenes = [];
    
    // Scene 0: Introduction - Why this analysis matters
    scenes.push({
      type: 'introduction',
      title: 'What We Discovered',
      content: 'We analyzed your brand\'s web site to learn about you and your business. Here is what we discovered while our AI does a deeper dive.',
      backgroundImage: brandData.metadata?.images?.[0]?.url,
      icon: Search
    });
    
    // Scene 1: Brand Name + Hero Image
    if (brandData.brand_data.brand_name) {
      scenes.push({
        type: 'hero',
        title: brandData.brand_data.brand_name,
        content: 'Welcome to your brand analysis',
        backgroundImage: brandData.metadata?.images?.[0]?.url,
        icon: Star
      });
    }
    
    // Scene 2: Elevator Pitch
    if (brandData.brand_data.elevator_pitch) {
      scenes.push({
        type: 'elevator_pitch',
        title: 'Your Brand Story',
        content: brandData.brand_data.elevator_pitch,
        backgroundImage: brandData.metadata?.images?.[1]?.url || brandData.metadata?.images?.[0]?.url,
        icon: MessageSquare
      });
    }
    
    // Scene 3: Target Market
    if (brandData.brand_data.target_market) {
      scenes.push({
        type: 'target_market',
        title: 'Your Audience',
        content: brandData.brand_data.target_market,
        badges: [brandData.brand_data.b2b_b2c, brandData.brand_data.target_geography].filter(Boolean),
        backgroundImage: brandData.metadata?.images?.[2]?.url || brandData.metadata?.images?.[0]?.url,
        icon: Users
      });
    }
    
    // Scene 4: Value Props & Differentiators
    if (brandData.brand_data.value_props || brandData.brand_data.differentiators) {
      scenes.push({
        type: 'value_props',
        title: 'What Makes You Unique',
        content: brandData.brand_data.value_props || brandData.brand_data.differentiators,
        backgroundImage: brandData.metadata?.images?.[3]?.url || brandData.metadata?.images?.[0]?.url,
        icon: Zap
      });
    }
    
    // Scene 5: Products & Services
    if (brandData.brand_data.products_overview) {
      scenes.push({
        type: 'products',
        title: 'Your Offerings',
        content: brandData.brand_data.products_overview,
        badge: brandData.brand_data.niche,
        backgroundImage: brandData.metadata?.images?.[4]?.url || brandData.metadata?.images?.[0]?.url,
        icon: Package
      });
    }
    
    return scenes;
  };

  const slideScenes = getSlideScenes();

  // Auto-advance slideshow
  useEffect(() => {
    // Clear any existing interval first
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = null;
    }
    
    // Only start new interval if auto-playing and we have multiple slides
    if (isSlideAutoPlaying && slideScenes.length > 1) {
      slideIntervalRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % slideScenes.length);
      }, 6000); // Slowed down from 4000ms to 6000ms
    }
    
    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
        slideIntervalRef.current = null;
      }
    };
  }, [isSlideAutoPlaying, slideScenes.length]);

  // Fetch AI-generated deeper insights
  const fetchAIAnalysis = async () => {
    // Check if analysis already exists
    if (brandData.brand_analysis_results) {
      console.log('[BrandAnalysis] Using cached AI analysis for brand:', brandData.id);
      setAiAnalysis(brandData.brand_analysis_results);
      setAiLoading(false);
      return;
    }

    setAiLoading(true);
    setAiError(null);
    
    try {
      const workersUrl = import.meta.env.VITE_WORKERS_URL || 'http://localhost:3000';
      const endpoint = isAnonymous 
        ? '/rainfactory/anonymous-brands/generate_narrative_analysis'
        : '/rainfactory/brand-genius';
      
      console.log('[BrandAnalysis] Fetching AI analysis for brand:', brandData.id);
      
      const requestBody = isAnonymous ? {
        brandIdentifier: brandData.id,
        rawData: brandData.scraping_results || {
          brand_data: brandData.brand_data,
          metadata: brandData.metadata,
          url: brandData.url
        }
      } : {
        action: 'generate_narrative_analysis',
        brandId: brandData.id,
        rawData: brandData.scraping_results || {
          brand_data: brandData.brand_data,
          metadata: brandData.metadata,
          url: brandData.url
        }
      };

      // Get authentication headers for authenticated requests
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (!isAnonymous) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }
      }

      const response = await fetch(`${workersUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const aiResult = await response.json();
        console.log('[BrandAnalysis] AI Analysis result:', aiResult);
        setAiAnalysis(aiResult);
      } else {
        const errorResult = await response.json();
        console.error('[BrandAnalysis] AI Analysis error:', errorResult);
        setAiError(errorResult.error || 'Failed to generate deeper insights');
      }
    } catch (error) {
      console.error('[BrandAnalysis] Network error fetching AI analysis:', error);
      setAiError('Failed to generate deeper insights');
    } finally {
      setAiLoading(false);
    }
  };

  // Start AI analysis when brand data is loaded
  useEffect(() => {
    if (brandData && !aiAnalysis && !aiLoading) {
      fetchAIAnalysis();
    }
  }, [brandData]);

  const toggleSlideAutoPlay = () => {
    setIsSlideAutoPlaying(prev => !prev);
  };

  const goToSlide = (index: number) => {
    // Clear any existing interval immediately
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = null;
    }
    
    // Update slide and stop auto-playing
    setCurrentSlide(index);
    setIsSlideAutoPlaying(false);
  };

  const goToPreviousSlide = () => {
    if (slideScenes.length === 0) return;
    const previousIndex = currentSlide === 0 ? slideScenes.length - 1 : currentSlide - 1;
    goToSlide(previousIndex);
  };

  const goToNextSlide = () => {
    if (slideScenes.length === 0) return;
    const nextIndex = (currentSlide + 1) % slideScenes.length;
    goToSlide(nextIndex);
  };

  const proceedToDeeperInsights = () => {
    // Only proceed if AI analysis is complete
    if (!aiLoading && aiAnalysis) {
      setShowDeeperInsights(true);
      setIsSlideAutoPlaying(false);
    }
  };

  const handleClaimBrand = () => {
    if (onClaimBrand) {
      onClaimBrand();
    }
  };

  return (
    <div className="min-h-screen bg-black overflow-x-hidden">
      {/* Immersive Brand Overview Slideshow */}
      {!showDeeperInsights && slideScenes.length > 0 && (
        <div className="relative min-h-screen w-full">
          {/* Background Image */}
          {slideScenes[currentSlide]?.backgroundImage && (
            <div 
              className="absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-in-out pointer-events-none"
              style={{ 
                backgroundImage: `url(${slideScenes[currentSlide].backgroundImage})`,
              }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-40" />
            </div>
          )}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60 pointer-events-none" />
          
          {/* Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 md:px-6">
            {/* The loader box is reused to show the continue button when ready */}

            {/* Slideshow Content */}
            <div className="max-w-4xl mx-auto text-center text-white">
              <div className="animate-fade-in">
                {/* Icon */}
                {slideScenes[currentSlide]?.icon && (
                  <div className="mb-3 md:mb-6">
                    {(() => {
                      const IconComponent = slideScenes[currentSlide].icon;
                      return IconComponent ? <IconComponent className="h-8 w-8 md:h-16 md:w-16 mx-auto text-white/80 mb-2 md:mb-4" /> : null;
                    })()}
                  </div>
                )}
                
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-3 md:mb-6 leading-tight">
                  {slideScenes[currentSlide]?.title}
                </h1>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-4 md:mb-8 leading-relaxed max-w-3xl mx-auto">
                  {slideScenes[currentSlide]?.content}
                </p>
                
                {/* Badges */}
                {slideScenes[currentSlide]?.badges && (
                  <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-4 md:mb-8">
                    {slideScenes[currentSlide].badges.map((badge, index) => (
                      <Badge key={index} variant="secondary" className="text-sm md:text-lg px-3 py-1.5 md:px-4 md:py-2">
                        {badge}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {slideScenes[currentSlide]?.badge && (
                  <div className="mb-4 md:mb-8">
                    <Badge variant="secondary" className="text-sm md:text-lg px-3 py-1.5 md:px-4 md:py-2">
                      {slideScenes[currentSlide].badge}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            {/* Centered world-class loader below content OR continue button in the same box */}
            {(aiLoading || (!aiLoading && aiAnalysis)) && (
              <div className="mt-6 md:mt-10 w-full max-w-2xl mx-auto">
                <div className="relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-5 py-4 md:px-6 md:py-5 shadow-2xl">
                  {aiLoading ? (
                    <>
                      <div className="flex items-center justify-center gap-3 md:gap-4">
                        <Loader2 className="h-6 w-6 md:h-7 md:w-7 text-white animate-spin" />
                        <div className="text-left">
                          <p className="text-base md:text-lg font-semibold">Creating Personalized Insights</p>
                          <p className="text-xs md:text-sm text-white/70">Analyzing your brand and preparing recommendations…</p>
                        </div>
                      </div>
                      <div className="mt-4 h-2 w-full bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full w-1/2 bg-gradient-to-r from-indigo-400 via-blue-400 to-cyan-400 animate-pulse rounded-full" />
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-center gap-4">
                      <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                        Your brand deep dive is ready
                      </div>
                      <RainbowButton
                        onClick={proceedToDeeperInsights}
                        className="text-xl px-8 py-4 shadow-2xl hover:scale-105 transition-transform"
                      >
                        Continue to Deeper Insights
                        <ArrowRight className="h-6 w-6 ml-2" />
                      </RainbowButton>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Slideshow Controls */}
          <div className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-3 md:gap-4 z-20">
            {/* Progress Indicators */}
            <div className="flex gap-1.5 md:gap-2">
              {slideScenes.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToSlide(index);
                  }}
                  className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 hover:scale-110 ${
                    index === currentSlide ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            
            {/* Play/Pause */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleSlideAutoPlay();
              }}
              className="p-1.5 md:p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors hover:scale-110"
              aria-label={isSlideAutoPlaying ? "Pause slideshow" : "Play slideshow"}
            >
              {isSlideAutoPlaying ? (
                <Pause className="h-4 w-4 md:h-5 md:w-5 text-white" />
              ) : (
                <Play className="h-4 w-4 md:h-5 md:w-5 text-white" />
              )}
            </button>
          </div>
          
          {/* Navigation Arrows */}
          {slideScenes.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToPreviousSlide();
                }}
                className="absolute left-4 md:left-6 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-white/20 rounded-full hover:bg-white/30 transition-all hover:scale-110 z-20"
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToNextSlide();
                }}
                className="absolute right-4 md:right-6 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-white/20 rounded-full hover:bg-white/30 transition-all hover:scale-110 z-20"
                aria-label="Next slide"
              >
                <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </button>
            </>
          )}
          
          {/* AI analysis loader now centered under content; top and corner loaders removed */}
        </div>
      )}
      
      {/* Deeper Insights Section - Interactive Slideshow */}
      {showDeeperInsights && aiAnalysis && (
        <BrandAnalysisSlideshow
          aiAnalysis={aiAnalysis}
          brandName={brandData?.brand_data?.brand_name || brandData?.brand_name || "Your Brand"}
          onClaimBrand={showClaimButton ? handleClaimBrand : () => onComplete?.()}
          isAnonymous={isAnonymous}
        />
      )}

      {/* AI Loading State for Deeper Insights */}
      {showDeeperInsights && aiLoading && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <h2 className="text-2xl font-bold mb-2">Preparing Your Personalized Insights</h2>
            <p className="text-muted-foreground">
              Our AI is analyzing your brand data to create a comprehensive slideshow presentation...
            </p>
          </div>
        </div>
      )}

      {/* AI Error State for Deeper Insights */}
      {showDeeperInsights && aiError && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Analysis Failed</h2>
            <p className="text-muted-foreground mb-6">
              {aiError}
            </p>
            <Button onClick={() => setShowDeeperInsights(false)}>
              Go Back
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}